import { useEffect, useState } from 'react';

export type Countdown = {
  /** Human-readable label like "3h 14m left" or "Ended". */
  label: string;
  /** True when fewer than 6 hours remain — UI uses this to render in red. */
  urgent: boolean;
  /** True when the deadline has passed. */
  ended: boolean;
};

/**
 * Returns a live-updating countdown to the given ISO timestamp.
 *
 * Updates every second. Component unmount clears the interval, so this is
 * safe to use in lists where cards mount/unmount frequently.
 */
export function useCountdown(endsAt: string): Countdown {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const ms = new Date(endsAt).getTime() - now;
  if (ms <= 0) return { label: 'Ended', urgent: true, ended: true };

  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    return { label: `${days}d ${hours % 24}h left`, urgent: false, ended: false };
  }
  if (hours >= 1) {
    return { label: `${hours}h ${minutes}m left`, urgent: hours < 6, ended: false };
  }
  return { label: `${minutes}m ${seconds}s left`, urgent: true, ended: false };
}
